import { FeedbackState } from "@c/shared";
import { cn } from "@u/cn";
import { Panel } from "@ui/panel";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DashboardFilters } from "./DashboardFilters";
import { DashboardPagination } from "./DashboardPagination";
import { DashboardSortSelect } from "./DashboardSortSelect";
import { DashboardToolbar } from "./DashboardToolbar";
import { DashboardViewSwitcher } from "./DashboardViewSwitcher";
import {
  useCollectionPlumbing,
  type DashboardCollectionProps,
} from "./useCollection";

/**
 * Shared chrome for the non-tabular collections (`DashboardList`,
 * `DashboardGrid`): toolbar + error/loading/empty state switch + pagination.
 * The two variants differ only in the outer wrapper, the loading skeleton and
 * how the items lay out — those come in via props so the rendered markup for
 * each stays identical to the hand-written versions.
 */
export function CollectionShell<T>({
  props,
  variant,
  toolbarClassName,
  feedbackSurface,
  skeleton,
  renderBody,
}: {
  props: DashboardCollectionProps<T>;
  variant: "list" | "grid";
  toolbarClassName?: string;
  feedbackSurface?: "plain";
  /** Loading placeholder shown while `props.isLoading`. */
  skeleton: ReactNode;
  /** Lays out the resolved page of items (grid vs. list surface). */
  renderBody: (pageItems: T[]) => ReactNode;
}) {
  const { t } = useTranslation();
  const { pageItems, total, query, state, isFiltering } =
    useCollectionPlumbing(props);

  const toolbar = (
    <DashboardToolbar
      className={toolbarClassName}
      search={
        props.searchable
          ? {
              value: state.search,
              onChange: query.setSearch,
              placeholder: props.searchPlaceholder,
            }
          : undefined
      }
      searchLeading={props.searchLeading}
      searchTrailing={props.searchTrailing}
      filters={
        props.filtersSlot ??
        (props.filters && props.filters.length > 0 ? (
          <DashboardFilters
            filters={props.filters}
            selected={state.filters}
            onChange={query.setFilter}
            onReset={query.reset}
          />
        ) : undefined)
      }
      sort={
        props.sortOptions || props.viewSwitcher ? (
          <>
            {props.sortOptions && props.sortOptions.length > 0 && (
              <DashboardSortSelect
                options={props.sortOptions}
                value={state.sort}
                onChange={query.setSort}
              />
            )}
            {props.viewSwitcher && (
              <DashboardViewSwitcher
                value={props.viewSwitcher.value}
                options={props.viewSwitcher.options}
                onChange={query.setView}
              />
            )}
          </>
        ) : undefined
      }
      actions={props.toolbarActions}
      onRefresh={props.onRefresh}
      createButton={props.createButton}
    />
  );

  const stateOrBody = props.error ? (
    <FeedbackState variant="error" surface={feedbackSurface} title={props.error} />
  ) : props.isLoading ? (
    skeleton
  ) : pageItems.length === 0 ? (
    <FeedbackState
      variant={isFiltering ? "no-results" : "empty"}
      surface={feedbackSurface}
      title={
        isFiltering
          ? t("ds_no_results", { defaultValue: "No results" })
          : (props.empty?.title ??
            t("ds_empty", { defaultValue: "Nothing here yet" }))
      }
      description={isFiltering ? undefined : props.empty?.description}
      primaryAction={isFiltering ? undefined : props.empty?.action}
      secondaryAction={isFiltering ? undefined : props.empty?.secondaryAction}
    />
  ) : (
    renderBody(pageItems)
  );

  const pagination = (
    <DashboardPagination
      page={state.page}
      pageSize={state.pageSize}
      total={total}
      pageSizeOptions={props.pageSizeOptions}
      onPage={query.setPage}
      onPageSize={query.setPageSize}
      className={variant === "grid" ? "border-t-0" : undefined}
    />
  );

  if (variant === "list") {
    return (
      <Panel
        surface={props.surface}
        className={cn("p-0 overflow-hidden", props.className)}
      >
        {toolbar}
        <div className="divide-y divide-black/5 dark:divide-white/5">
          {stateOrBody}
        </div>
        {pagination}
      </Panel>
    );
  }

  return (
    <div className={cn("space-y-4", props.className)}>
      {toolbar}
      {stateOrBody}
      <Panel className="p-0 overflow-hidden">{pagination}</Panel>
    </div>
  );
}
